import * as React from "react";
import type { Editor as TinyMCEEditor } from "tinymce";
import { UIModel } from "../../../../model/ui-model";
import { RoleBasedPermissionedContentEditModule } from "../custom-modules/RoleBasedPermissionedContentModule";
import { DEFAULT_CONFIG } from "./tinymce-config/default";
import { TinyMCEBaseEditor } from "./TinyMCEBaseEditor";
import { AppContext } from "../../../../setup/context";
import { Preference } from "../../../../setup/preference";
import { DEV_MODE_CONFIG } from "./tinymce-config/dev-mode-config";

export const StrongRichTextEdit = (props: {
    content: string;
    onChange: (v: string) => void;
    hidePermContent?: boolean;
}) => {
    const context = React.useContext(AppContext);
    const permissionContentActive = UIModel.State.use<boolean>({ initialValue: false });
    const [editor, setEditor] = React.useState<TinyMCEEditor | null>(null);

    const getTinyConfig = () => {
        let config = DEFAULT_CONFIG;
        const setupCallbacks: Array<(editor: TinyMCEEditor) => void> = [];

        if (context.getPreference().labs.has(Preference.Lab.DevMode)) {
            config = DEV_MODE_CONFIG;
        }

        // Preserve base config's setup (e.g., markdown paste detection)
        if (config.setup) {
            setupCallbacks.push(config.setup as (editor: TinyMCEEditor) => void);
        }

        const stage = context.config.stage.toLowerCase();
        const spellingUrl = context.config.apiEndpoint.spellingDomain ||
            (stage === "prod"
                ? "https://api.pathfinder.legal.amazon.dev/spelling/"
                : `https://api.${stage}.pathfinder.legal.amazon.dev/spelling/`);

        config = {
            ...config,
            plugins: [
                ...config.plugins,
                "advtable",
                "tinymcespellchecker",
                "autocorrect"
            ],
            toolbar: config.toolbar + " | advtablerownumbering",
            spellchecker_rpc_url: spellingUrl,
            spellchecker_language: "en-US",
            autocorrect_service_url: spellingUrl,
            autocorrect_autocorrect: true,
            autocorrect_capitalize: true
        } as typeof config;

        setupCallbacks.push((e) => {
            e.on("init", () => {
                const style = document.createElement("style");
                style.id = "tinymce-notification-fix";
                style.textContent = `
                    .tox-notification.tox-notification--in {
                        display: flex !important;
                    }
                `;
                document.head.appendChild(style);
            });
        });

        setupCallbacks.push((e) => {
            if (props.hidePermContent) return;
            e.ui.registry.addButton("permissionedContentButton", {
                icon: "lock",
                text: "Permissioned Content",
                onAction: val => permissionContentActive.setValue(true)
            });
        });

        config = {
            ...config,
            toolbar: config.toolbar + "|permissionedContentButton",
            setup: (e) => setupCallbacks.forEach(cb => cb(e)),
            paste_preprocess: (plugin, args) => {
                let restoreHtml = args.content;
                restoreHtml = restoreHtml.replaceAll("&lt;", "<");
                restoreHtml = restoreHtml.replaceAll("&gt;", ">");
                restoreHtml = restoreHtml.replaceAll("&quot;", `"`);
                args.content = restoreHtml;
            }
        } as typeof config;

        return config;
    };
    return (
        <React.Fragment>
            <RoleBasedPermissionedContentEditModule active={permissionContentActive} editor={editor}/>
            <TinyMCEBaseEditor
                onChange={props.onChange}
                value={props.content}
                onInit={e => setEditor(e)}
                config={getTinyConfig()}
            />
        </React.Fragment>
    );
};